import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { giroTechLogo, giroTechPlatformName } from "../../utils/constants";

// Determina a URL base da API
const getApiBaseUrl = () => {
  // Em desenvolvimento, usa proxy configurado no craco.config.js
  if (process.env.NODE_ENV === "development") {
    return "/api";
  }

  // Em produção, usa window.REACT_APP_BACKEND_URL (injetado pelo entrypoint.sh)
  const envUrl = window.REACT_APP_BACKEND_URL;

  // Se envUrl está vazio ou é undefined, usa URL relativa para produção
  if (!envUrl || envUrl === "") {
    return "/api";
  }

  // Se a URL já termina com /api, usa como está
  if (envUrl.endsWith("/api")) {
    return envUrl;
  }

  // Se a URL não tem /api, adiciona
  return envUrl + "/api";
};

const API_BASE_URL = getApiBaseUrl();

const LoginForm = ({ onSuccess, onRegisterClick, successMessage }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // 'email' or 'code'
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setCodeSent(true);
        setStep("code");
      } else {
        setError(data.detail || "Erro ao enviar código");
      }
    } catch (error) {
      console.error("Send code error:", error);
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          verification_code: code,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Passar tokens JWT para o callback
        onSuccess(data.user, {
          access_token: data.access_token,
          refresh_token: data.refresh_token
        });
      } else {
        setError(data.detail || "Código inválido");
      }
    } catch (error) {
      console.error("Verify code error:", error);
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setCode("");
    setCodeSent(false);
    setError("");
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setError("");
        alert("Código reenviado com sucesso!");
      } else {
        setError(data.detail || "Erro ao reenviar código");
      }
    } catch (error) {
      console.error("Resend code error:", error);
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCertificate = () => {
    navigate("/verify-certificate");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-center">
            <div className="flex justify-center mb-6">
              <img
                src="https://giro-logos.s3.us-east-1.amazonaws.com/public/logo-branco.png"
                alt="Giro.Tech"
                className="h-20 w-auto"
              />
            </div>
            <h1 className="text-white text-2xl font-bold mb-2">Entrar</h1>
            <p className="text-blue-100 text-sm">{giroTechPlatformName}</p>
          </div>

          {/* Form */}
          <div className="p-8">
            {successMessage && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                {successMessage}
              </div>
            )}

            {step === "email" && (
              <form onSubmit={handleSendCode} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="seu@email.com"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                >
                  {loading ? "Enviando..." : "Enviar código"}
                </button>
              </form>
            )}

            {step === "code" && (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código de verificação
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-xl font-mono tracking-widest transition-all duration-200"
                    placeholder="000000"
                    maxLength="6"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Código enviado para <span className="font-medium">{email}</span>
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                >
                  {loading ? "Verificando..." : "Verificar e entrar"}
                </button>

                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors duration-200"
                  >
                    ← Voltar
                  </button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200 disabled:opacity-50"
                  >
                    Reenviar código
                  </button>
                </div>
              </form>
            )}

            <div className="mt-8 text-center space-y-3">
              <button
                onClick={onRegisterClick}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200 border border-blue-200 hover:border-blue-300 px-4 py-2 rounded-lg"
              >
                Não tem conta? Criar uma
              </button>
              <div>
                <button
                  onClick={handleVerifyCertificate}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors duration-200 border border-gray-200 hover:border-gray-300 px-4 py-2 rounded-lg"
                >
                  Quero validar um certificado
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
